/* 
Mono
console.log('⏳ evaluating', import.meta.url);
*/

// Import project modules
import { isValidDisplayName, normalizeEmail, qS, pageLanguage, publicPage, url } from './_default.js';
import { activity } from './activity.js';
import { error } from './error.js';
import { user } from './user.js';
import { auth } from './auth.js';
import { getLocalStorageItem } from './storage.js';
import { redirectToSsoIfRequired } from './sso.js';
import { t } from './app/appTranslation.js';
import { currency } from './currency.js';
import { locationUnavailableFromStoredValue } from './currencyLocationState.mjs';
import { countryHasFreeMessages } from './freeMessageCountries.mjs';

const recaptchaBlockedErrorMessage = publicPage?.text?.signUpForm?.recaptchaBlockedErrorMessage || 'Google reCAPTCHA could not be loaded. Ad blockers and privacy extensions can block it. Please allow mono.chat and google.com, then try again.';
const recaptchaLoadingErrorMessage = publicPage?.text?.signUpForm?.recaptchaLoadingErrorMessage || 'Security check is still loading. Please wait a moment and try again.';
const requestTimeoutErrorMessage = publicPage?.text?.signUpForm?.requestTimeoutErrorMessage || 'Something went wrong. Turn off your ad blocker, and try again.';
const honeypotErrorMessage = publicPage?.text?.signUpForm?.honeypotErrorMessage || 'Something went wrong. Please try again.';

const renderSignUpLead = function() {
  const signUpLead = qS('#sign_up_lead');
  if (!signUpLead) return;

  const country = getLocalStorageItem(currency.localStorageItemNameCountry);
  const copy = countryHasFreeMessages(country)
    ? publicPage?.text?.auth?.signUpLead
    : publicPage?.text?.auth?.signUpLeadNoAllowance;
  if (copy) signUpLead.textContent = copy;
};

const resetSignUpButton = function() {
  const signUpButton = qS('#sign_up_next');
  if(!signUpButton){
    return;
  }

  signUpButton.classList.remove('button-loading');
  signUpButton.disabled = false;
};

const showRecaptchaBlockedError = function() {
  const errorMessage = window.recaptchaLoadState === 'loading' ? recaptchaLoadingErrorMessage : recaptchaBlockedErrorMessage;

  error.handle({
    error: 'RECAPTCHA_BLOCKED',
    errorMessage
  }, 'sign_up_terms_container');
  resetSignUpButton();
};

const showTimeoutError = function() {
  error.handle({
    error: 'TIMEOUT_ERROR',
    errorMessage: requestTimeoutErrorMessage
  }, 'sign_up_terms_container');
  resetSignUpButton();
};

const isTimeoutError = function(err) {
  if(typeof err === 'string'){
    return err === 'Timeout';
  }

  if(!err || typeof err !== 'object'){
    return false;
  }

  return err.message === 'Timeout' || err.code === 'functions/deadline-exceeded';
};

const isBrowserInteropUpdateError = function(err) {
  const message = typeof err === 'string' ? err : err?.message;
  if(typeof message !== 'string'){
    return false;
  }

  return message.startsWith('Object Not Found Matching Id:') && message.includes('MethodName:update');
};

const initSignUpRecaptchaGuard = function() {
  const signUpForm = qS('#sign_up_email_form');
  if(!signUpForm){
    return;
  }

  signUpForm.addEventListener('submit', (event) => {
    const recaptchaReady = typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.execute === 'function';
    if(recaptchaReady){
      return;
    }

    event.preventDefault();

    // Retry loading in case extensions or network timing delayed script availability.
    if(typeof window.loadRecaptcha === 'function'){
      window.loadRecaptcha();
    }

    showRecaptchaBlockedError();
  });
};

const initSignUpUnhandledRejectionGuard = function() {
  const signUpForm = qS('#sign_up_email_form');
  if(!signUpForm){
    return;
  }

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;

    if(isTimeoutError(reason)){
      // reCAPTCHA/Firebase can reject with raw "Timeout" outside local catch blocks.
      event.preventDefault?.();
      showTimeoutError();
      return;
    }

    if(isBrowserInteropUpdateError(reason)){
      // Ignore known browser-extension interop noise on the auth page.
      event.preventDefault?.();
    }
  });
};

initSignUpRecaptchaGuard();
initSignUpUnhandledRejectionGuard();

const isWebkitAutofilled = function(element) {
  if(!element || typeof element.matches !== 'function'){
    return false;
  }

  try {
    return element.matches(':-webkit-autofill');
  } catch (_err) {
    return false;
  }
};

const signUp = async function(recaptchaToken) {
  qS(`#sign_up_next`).classList.add('button-loading');

  if(!recaptchaToken){
    showRecaptchaBlockedError();
    return;
  }

  const publicId = getLocalStorageItem('publicId') || null;
  const usedPublicIdList = getLocalStorageItem('usedPublicIdList') || null;

  // Save attribution from local storage
  // Is saved in native app
  // And on home when ?ref= or ?utm_source= is set
  let attribution = JSON.parse(getLocalStorageItem('utmAttribution')) || null;

  // Split test group is set by URL parameter g=
  // adId is set by native app
  const splitTestGroup = getLocalStorageItem('splitTestGroup') || null;

  // Save tag
  const storedTagList = getLocalStorageItem('tag');
  const tagList = storedTagList ? storedTagList.split(',') : [];

  // Display name
  const displayName = qS('#sign_up_display_name').value || null;

  // Hidden honey pot
  const podInput = qS('#sign_up_pod');
  const pod = (podInput?.value || '').trim();
  if(pod && pod.length > 0){
    // Chrome Android can autofill hidden fields and trigger false positives.
    if(isWebkitAutofilled(podInput)){
      podInput.value = '';
    }else{
      // Honey pot detected
      error.handle({error: honeypotErrorMessage}, 'sign_up_terms_container');
      resetSignUpButton();
      activity.log(activity.activityTypes.signupHoneyPot, { email: normalizeEmail(qS('#sign_up_email').value), displayName: displayName, pod: pod });
      return;
    }
  }

  if(!displayName || displayName.length === 0){
    error.handle({error: publicPage?.text?.signUpForm?.fullNameRequiredError || 'Please enter your full name'}, 'sign_up_display_name_container');
    resetSignUpButton();
    return;
  }

  if(displayName.length >= 40){
    error.handle({error: t('signUpForm.fullNameTooLongError')}, 'sign_up_display_name_container');
    resetSignUpButton();
    return;
  }

  if(!isValidDisplayName(displayName)){
    error.handle({error: t('signUpForm.fullNameInvalidError')}, 'sign_up_display_name_container');
    resetSignUpButton();
    return;
  }

  // Terms of use
  if(!qS('#sign_up_terms').checked){
    error.handle({error: publicPage?.text?.signUpForm?.acceptTermsError || 'Please accept the terms of use'}, 'sign_up_terms_container');
    resetSignUpButton();
    return;
  }

  qS(`#sign_up_next`).disabled = true;

  try {
    const language = pageLanguage || (navigator?.language?.startsWith('de') ? 'de' : 'en');

    const email = normalizeEmail(qS('#sign_up_email').value);
    await currency.get();
    renderSignUpLead();
    const locationUnavailable = locationUnavailableFromStoredValue(
      getLocalStorageItem(currency.localStorageItemNameLocationUnavailable)
    );
    const freeMessageCountry = getLocalStorageItem(currency.localStorageItemNameCountry);

    console.log('displayName', displayName);
    console.log('email.value', email);

    const redirectedToSso = await redirectToSsoIfRequired({
      email,
      flow: 'signUp',
      redirect: url.parameters.redirect || '/app/',
      pending: {
        publicId,
        usedPublicIdList,
        splitTestGroup,
        attribution,
        displayName,
        tagList,
        language,
        locationUnavailable,
        freeMessageCountry,
      },
    });
    if (redirectedToSso) {
      return;
    }
    
    const result = await user.create(email, {recaptchaToken, publicId, usedPublicIdList, splitTestGroup, attribution, displayName, tagList, language, locationUnavailable, freeMessageCountry});
    console.log('result user.create', result);
    if (result.success) {
      await activity.log(activity.activityTypes.signup);
      const verifyHref = publicPage?.links?.verify?.href || '/verify';
      window.location.href = `${verifyHref}?email=${encodeURIComponent(email)}`;
    } else {
      console.log('result.error', result.error);
      
      // Show the error where it belongs
      let container;
      if([
        'DISPLAY_NAME_REQUIRED',
        'DISPLAY_NAME_INVALID',
        'DISPLAY_NAME_FIRST_AND_LAST_REQUIRED',
        'DISPLAY_NAME_LINK_OR_CODE_NOT_ALLOWED',
      ].includes(result.error)){
        container = 'sign_up_display_name_container';
      }else if([
        'RECAPTCHA_TOKEN_REQUIRED',
        'RECAPTCHA_VALIDATION_FAILED',
        'BELOW_RECAPTCHA_MINIMUM_SCORE'
      ].includes(result.error)){
        container = 'sign_up_terms_container';
      }else if(result.error === 'SSO_REQUIRED'){
        container = 'sign_up_email_container';
      }else{
        // If not display name, it's probably a problem with the email address
        container = 'sign_up_email_container';
      }

      error.handle(result, container);
      resetSignUpButton();
    }
  } catch (err) {
    if(isTimeoutError(err)){
      showTimeoutError();
      return;
    }

    error.handle({ error: publicPage?.text?.signUpForm?.genericError || 'Something went wrong. Please try again.' }, 'sign_up_terms_container');
    resetSignUpButton();
    console.error('signUp error', err);
  }
}

const authSignUp = function(recaptchaToken) {
  Promise.resolve(signUp(recaptchaToken)).catch((err) => {
    if(isTimeoutError(err)){
      showTimeoutError();
      return;
    }

    if(typeof err === 'string' && err === 'RECAPTCHA_ERROR'){
      showRecaptchaBlockedError();
      return;
    }

    error.handle({ error: publicPage?.text?.signUpForm?.genericError || 'Something went wrong. Please try again.' }, 'sign_up_terms_container');
    resetSignUpButton();
    console.error('authSignUp callback error', err);
  });
};

const authSignUpRecaptchaError = function() {
  showRecaptchaBlockedError();
};

const authSignUpRecaptchaExpired = function() {
  error.handle({
    error: 'RECAPTCHA_EXPIRED',
    errorMessage: publicPage?.text?.signUpForm?.recaptchaExpired || 'Security check expired. Please try again.'
  }, 'sign_up_terms_container');
  resetSignUpButton();
};

// For recaptcha callbacks
window.authSignUp = authSignUp;
window.authSignUpRecaptchaError = authSignUpRecaptchaError;
window.authSignUpRecaptchaExpired = authSignUpRecaptchaExpired;

currency.get().catch(() => null).finally(renderSignUpLead);

export { signUp };
